import { defineStore } from 'pinia'
import type {
  EditOperation,
  EditorState,
  SelectedElement,
} from './helper'
import {
  canRedo,
  canUndo,
  cleanEditingContent,
  defaultEditorState,
  getCurrentContent,
  sendMessageToIframe,
} from './helper'

import { store } from '@/store'

export const useEditorStore = defineStore('editor-store', {
  state: (): EditorState => defaultEditorState(),

  getters: {
    // 是否可以撤销
    canGlobalUndo: state => canUndo(state.globalEditHistory),

    // 是否可以重做
    canGlobalRedo: state => canRedo(state.globalEditHistory),

    // 获取未保存修改的页面数量
    unsavedPagesCount: state => Object.keys(state.tempEditStates).length,

    // 获取选中元素的信息
    selectedElementInfo: (state) => {
      if (!state.selectedElement) {
        return null
      }
      return {
        tagName: state.selectedElement.tagName,
        id: state.selectedElement.id,
        className: state.selectedElement.className,
      }
    },

    // 当前页面状态信息
    currentPageInfo: state => ({
      visiblePageIndex: state.visiblePageIndex,
      visiblePageId: state.visiblePageId,
    }),
  },

  actions: {
    setIsConfirmUnsave(isConfirmUnsave: boolean) {
      this.isConfirmUnsave = isConfirmUnsave
    },

    setToUrl(toUrl: string) {
      this.toUrl = toUrl
    },

    setShowUnsaveDialog(showUnsaveDialog: boolean) {
      this.showUnsaveDialog = showUnsaveDialog
    },

    // 设置编辑模式
    setEditMode(isEditMode: boolean) {
      this.isEditMode = isEditMode
      if (!isEditMode) {
        this.clearSelection()
      }
    },

    // 设置选中元素
    setSelectedElement(element: SelectedElement | null, pageIndex = -1) {
      this.selectedElement = element
      this.selectedPageIndex = element ? pageIndex : -1
    },

    // 清除选择
    clearSelection() {
      this.selectedElement = null
      this.selectedPageIndex = -1
    },

    // 设置页面和 iframe 引用
    setPageRefs(pageRefs: { [key: string]: any }) {
      this.pageRefs = pageRefs
    },

    setPptListRef(pptListRef: any) {
      this.pptListRef = pptListRef
    },

    setIframeRefs(iframeRefs: { [index: number]: any }) {
      this.iframeRefs = iframeRefs
    },

    // 页面状态管理
    setVisiblePage(pageIndex: number, pageId: string) {
      this.visiblePageIndex = pageIndex
      this.visiblePageId = pageId
    },

    setVisiblePageIndex(pageIndex: number) {
      this.visiblePageIndex = pageIndex
    },

    setVisiblePageId(pageId: string) {
      this.visiblePageId = pageId
    },

    // 添加编辑操作到历史栈
    addEditOperation(operation: Omit<EditOperation, 'timestamp'>) {
      const timestamp = Date.now()
      const globalHistory = this.globalEditHistory

      // console.log('ELEMENT_UPDATED 检查:', {
      //   pageId: operation.pageId,
      //   currentContent: `${operation.previousContent.slice(0, 100)}...`,
      //   newContent: `${operation.htmlContent.slice(0, 100)}...`,
      //   isEqual: operation.previousContent === operation.htmlContent,
      // })

      // console.log('添加编辑操作到历史栈:', {
      //   operation,
      //   currentIndex: globalHistory.currentIndex,
      //   stackLength: globalHistory.stack.length,
      //   tempStatesKeys: Object.keys(this.tempEditStates),
      // })

      // 如果当前不在栈顶，删除后面的历史（分支历史）
      if (globalHistory.currentIndex < globalHistory.stack.length - 1) {
        console.log('删除分支历史，当前索引:', globalHistory.currentIndex, '栈长度:', globalHistory.stack.length)
        globalHistory.stack = globalHistory.stack.slice(0, globalHistory.currentIndex + 1)
      }

      // 添加新的历史记录
      const historyEntry: EditOperation = {
        ...operation,
        timestamp,
      }

      globalHistory.stack.push(historyEntry)
      globalHistory.currentIndex = globalHistory.stack.length - 1

      // console.log('添加历史记录:', {
      //   historyEntry,
      //   currentIndex: globalHistory.currentIndex,
      //   stackLength: globalHistory.stack.length,
      // })

      // 更新临时状态
      this.tempEditStates[operation.pageId] = {
        htmlContent: operation.htmlContent,
        lastModified: timestamp,
      }

      this.hasUnsavedChanges = true

      // console.log('更新后的临时状态:', {
      //   pageId: operation.pageId,
      //   tempStates: this.tempEditStates,
      //   hasUnsavedChanges: this.hasUnsavedChanges,
      // })

      // 限制历史记录数量
      if (globalHistory.stack.length > 100) {
        globalHistory.stack = globalHistory.stack.slice(-100)
        globalHistory.currentIndex = globalHistory.stack.length - 1
      }
    },

    // 处理元素更新
    handleElementUpdate(
      pageId: string,
      pageIndex: number,
      newHtmlContent: string,
      originalContent: string,
    ) {
      // 获取当前实际显示的内容
      const currentContent = getCurrentContent(pageId, this.tempEditStates, originalContent)

      if (currentContent !== newHtmlContent) {
        this.addEditOperation({
          pageId,
          pageIndex,
          htmlContent: newHtmlContent,
          previousContent: currentContent,
          operation: 'edit',
        })
      }
    },

    // 全局撤销
    globalUndo() {
      const globalHistory = this.globalEditHistory

      console.log('撤销前状态:', {
        currentIndex: globalHistory.currentIndex,
        stackLength: globalHistory.stack.length,
        stack: globalHistory.stack,
      })

      if (globalHistory.currentIndex >= 0) {
        const currentOperation = globalHistory.stack[globalHistory.currentIndex]
        const { pageId, previousContent } = currentOperation

        console.log('撤销操作:', {
          pageId,
          previousContent,
          currentOperation,
        })

        // 撤销当前操作：恢复到操作前的内容
        const htmlContent = previousContent

        // 找到对应的页面索引
        const pageIndex = Object.keys(this.pageRefs).findIndex(id => id === pageId)

        // 更新临时状态
        if (htmlContent !== this.pageRefs[pageId]?.htmlContent) {
          // 只有与原始内容不同时才标记为临时状态
          this.tempEditStates[pageId] = {
            htmlContent,
            lastModified: Date.now(),
          }
        } else {
          // 如果撤销到原始状态，清除临时状态
          delete this.tempEditStates[pageId]
        }

        // 发送消息给对应的 iframe 更新显示
        const iframe = this.iframeRefs[pageIndex]
        if (iframe) {
          sendMessageToIframe(iframe, {
            type: 'REPLACE_HTML',
            payload: { html: htmlContent },
          })
        }

        // 先移动全局历史指针，表示撤销了这个操作
        globalHistory.currentIndex--

        // 检查全局是否还有未保存的修改
        this.hasUnsavedChanges = Object.keys(this.tempEditStates).length > 0

        return {
          pageId,
          pageIndex,
          htmlContent,
          isUndo: true,
          isTemporary: htmlContent !== this.pageRefs[pageId]?.htmlContent,
        }
      }
      return null
    },

    // 全局重做
    globalRedo() {
      const globalHistory = this.globalEditHistory

      if (globalHistory.currentIndex < globalHistory.stack.length - 1) {
        // 移动全局历史指针到下一个操作
        globalHistory.currentIndex++

        const redoOperation = globalHistory.stack[globalHistory.currentIndex]
        const { pageId, htmlContent } = redoOperation

        // 找到对应的页面索引
        const pageIndex = Object.keys(this.pageRefs).findIndex(id => id === pageId)

        // 重做操作：应用操作后的内容
        // 更新临时状态
        if (htmlContent !== this.pageRefs[pageId]?.htmlContent) {
          // 只有与原始内容不同时才标记为临时状态
          this.tempEditStates[pageId] = {
            htmlContent,
            lastModified: Date.now(),
          }
        } else {
          // 如果重做到原始状态，清除临时状态
          delete this.tempEditStates[pageId]
        }

        // 发送消息给对应的 iframe 更新显示
        const iframe = this.iframeRefs[pageIndex]
        if (iframe) {
          sendMessageToIframe(iframe, {
            type: 'REPLACE_HTML',
            payload: { html: htmlContent },
          })
        }

        // 检查全局是否还有未保存的修改
        this.hasUnsavedChanges = Object.keys(this.tempEditStates).length > 0

        return {
          pageId,
          pageIndex,
          htmlContent,
          isRedo: true,
          isTemporary: htmlContent !== this.pageRefs[pageId]?.htmlContent,
        }
      }
      return null
    },

    // 准备保存数据
    prepareSaveData(pagesWithPageRefs: any[]) {
      return pagesWithPageRefs.map((page) => {
        const tempState = this.tempEditStates[page.id]
        const htmlContent = tempState ? tempState.htmlContent : page.htmlContent

        // 清理编辑时注入的内容
        const cleanedContent = cleanEditingContent(htmlContent)

        // 构建正确的数据结构，contentData 应该在 contentList[0].contentData 中
        const updatedItem = {
          ...page.originalItem,
        }

        // 确保 contentList 存在并更新第一个内容项的 contentData
        if (updatedItem.contentList && updatedItem.contentList.length > 0) {
          updatedItem.contentList = [
            {
              ...updatedItem.contentList[0],
              contentData: cleanedContent,
            },
            ...updatedItem.contentList.slice(1),
          ]
        } else {
          // 如果 contentList 不存在或为空，创建一个新的
          updatedItem.contentList = [
            {
              contentData: cleanedContent,
            },
          ]
        }

        return updatedItem
      })
    },

    // 保存完成后清理
    afterSave() {
      this.tempEditStates = {}
      this.globalEditHistory = {
        stack: [],
        currentIndex: -1,
      }
      this.hasUnsavedChanges = false
    },

    // 丢弃所有修改
    discardChanges() {
      // 恢复所有页面到保存的状态
      Object.keys(this.tempEditStates).forEach((pageId) => {
        const pageIndex = Object.keys(this.pageRefs).findIndex(id => id === pageId)
        if (pageIndex >= 0) {
          const page = this.pageRefs[pageId]
          // 恢复到保存的状态
          const iframe = this.iframeRefs[pageIndex]
          sendMessageToIframe(iframe, {
            type: 'REPLACE_HTML',
            payload: { html: page.htmlContent },
          })
        }
      })

      // 清空临时状态和全局历史栈
      this.tempEditStates = {}
      this.globalEditHistory = {
        stack: [],
        currentIndex: -1,
      }
      this.hasUnsavedChanges = false
    },

    // 清空所有状态
    clearAll() {
      this.isEditMode = false
      this.selectedPageIndex = -1
      this.selectedElement = null
      this.visiblePageIndex = 0
      this.visiblePageId = ''
      this.globalEditHistory = {
        stack: [],
        currentIndex: -1,
      }
      this.tempEditStates = {}
      this.hasUnsavedChanges = false
      this.pageRefs = {}
      this.iframeRefs = {}
    },

    // 检查页面是否有未保存的修改
    hasPageChanges(pageId: string): boolean {
      return !!this.tempEditStates[pageId]
    },

    // 获取页面的当前内容
    getPageCurrentContent(pageId: string, originalContent: string): string {
      return getCurrentContent(pageId, this.tempEditStates, originalContent)
    },
  },
})

export function useEditorStoreWithOut() {
  return useEditorStore(store)
}
